import type { StorageUsage } from "@lumora/bridge";
import { formatUsd, type UsageViewModel } from "@lumora/core";
import { useViewModel } from "@lumora/ui";
import {
  estimateTrafficCost,
  STARTER_INCLUDED_REQUESTS,
  TRAFFIC_CENTS_PER_1K,
  formatBytesGB,
} from "./platform/siteTrafficMetrics.js";
import { TrafficDailyBars } from "./platform/TrafficDailyBars.js";

function storageStatusText(storage: StorageUsage): string {
  if (storage.status === "exceeded") {
    const over = `Over by ${formatBytesGB(storage.overageBytes)}`;
    return storage.overagePolicy === "block"
      ? `${over} · uploads paused until storage is freed or the plan is raised`
      : over;
  }
  const left = `${formatBytesGB(storage.remainingBytes)} left`;
  return storage.status === "warning" ? `${left} · nearing limit` : left;
}

const n = (v: number): string => v.toLocaleString("en-US");

export interface UsageSectionProps {
  viewModel: UsageViewModel;
}

/** Owner portal traffic — graph + running cost (#438). */
export function UsageSection({ viewModel }: UsageSectionProps) {
  const state = useViewModel(viewModel);
  const cost = estimateTrafficCost(state.totalRequests);
  const includedUsed = Math.min(state.totalRequests, STARTER_INCLUDED_REQUESTS);
  const includedPct = (includedUsed / STARTER_INCLUDED_REQUESTS) * 100;
  const storage = state.storage;
  const ledger = state.ledger;
  const periodLabel =
    state.periodStart && state.periodEnd
      ? `${new Date(state.periodStart).toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${new Date(state.periodEnd).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
      : null;

  return (
    <section className="portal-section" data-lumora-view="usage">
      <h2>Traffic &amp; Usage</h2>

      {state.loading && state.rows.length === 0 ? <p>Loading…</p> : null}

      {state.error ? (
        <p className="portal-error" data-lumora-error>
          {state.error}
        </p>
      ) : null}

      <div className="portal-usage-kpis">
        <div>
          <p className="portal-usage-kpi-label">Requests</p>
          <p className="portal-usage-kpi-value" data-lumora-usage-total>
            {state.totalRequests.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="portal-usage-kpi-label">Running metered cost</p>
          <p className="portal-usage-kpi-value" data-lumora-usage-running-cost>
            {formatUsd(cost.listRateCents)}
          </p>
        </div>
        <div>
          <p className="portal-usage-kpi-label">Billable overage now</p>
          <p className="portal-usage-kpi-value">{formatUsd(cost.starterOverageCents)}</p>
        </div>
        {storage ? (
          <div>
            <p className="portal-usage-kpi-label">Storage</p>
            <p className="portal-usage-kpi-value" data-lumora-storage-used>
              {formatBytesGB(storage.usedBytes)} / {formatBytesGB(storage.quotaBytes)}
            </p>
          </div>
        ) : null}
      </div>

      {ledger ? (
        <div className="portal-usage-ledger" data-lumora-ledger={ledger.period}>
          <h3>This month</h3>
          <dl className="portal-usage-ledger-list">
            <div>
              <dt>API requests</dt>
              <dd data-lumora-ledger-api>
                {`${n(ledger.apiRequests)} / ${n(ledger.includedApiRequests)} included`}
              </dd>
            </div>
            <div>
              <dt>R2 operations (estimated)</dt>
              <dd data-lumora-ledger-r2>
                {`${n(ledger.r2ReadOpsEst + ledger.r2WriteOps)} (${n(ledger.r2ReadOpsEst)} reads, ${n(ledger.r2WriteOps)} writes)`}
              </dd>
            </div>
            <div>
              <dt>Storage</dt>
              <dd data-lumora-ledger-storage>
                {`${formatBytesGB(ledger.storageBytes)} / ${formatBytesGB(ledger.quotaBytes)}`}
              </dd>
            </div>
            <div>
              <dt>Projected invoice</dt>
              <dd data-lumora-ledger-total>{formatUsd(ledger.projected.totalCents)}</dd>
            </div>
          </dl>
        </div>
      ) : null}

      {periodLabel ? <p className="muted portal-usage-period">{periodLabel}</p> : null}

      <div className="portal-usage-allotment" aria-label="Starter included allotment">
        <div className="portal-usage-allotment-track">
          <div
            className="portal-usage-allotment-fill"
            style={{ width: `${Math.min(100, includedPct)}%` }}
          />
        </div>
        <p className="muted portal-usage-allotment-label">
          {includedUsed.toLocaleString()} / {STARTER_INCLUDED_REQUESTS.toLocaleString()} included
          (Starter)
          {cost.includedRemaining > 0
            ? ` · ${cost.includedRemaining.toLocaleString()} left`
            : " · allotment used — overage applies"}
        </p>
      </div>

      {storage ? (
        <div
          className="portal-usage-allotment portal-usage-allotment--storage"
          data-storage-status={storage.status}
          aria-label="Storage allotment"
        >
          <div className="portal-usage-allotment-track">
            <div
              className="portal-usage-allotment-fill"
              data-lumora-storage-bar
              style={{ width: `${Math.min(100, storage.percent)}%` }}
            />
          </div>
          <p className="muted portal-usage-allotment-label" data-lumora-storage-status>
            {storageStatusText(storage)}
          </p>
        </div>
      ) : null}

      <TrafficDailyBars
        byDay={state.rows}
        emptyLabel={
          state.loading ? "Loading daily traffic…" : "No requests recorded this period yet."
        }
      />

      {state.rows.length > 0 ? (
        <table className="portal-table" data-lumora-usage-table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Requests</th>
              <th>Day cost*</th>
            </tr>
          </thead>
          <tbody>
            {[...state.rows].reverse().slice(0, 14).map((row) => {
              const dayCost = estimateTrafficCost(row.requests).listRateCents;
              return (
                <tr key={row.date}>
                  <td>{row.date}</td>
                  <td>{row.requests.toLocaleString()}</td>
                  <td>{formatUsd(dayCost)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}

      <p className="muted portal-usage-footnote">
        *Day cost = requests × {formatUsd(TRAFFIC_CENTS_PER_1K)}/1k (list rate). Running
        metered cost is the same rate on the month total. Billable overage assumes Starter’s{" "}
        {STARTER_INCLUDED_REQUESTS.toLocaleString()} included. Your plan may differ.
      </p>
    </section>
  );
}
